<template>
    <!-- Loading -->
    <div v-if="loading" class="py-5 text-center">
        <Loading :show-text="false" spinner-class="spinner-grow" />
    </div>

    <template v-else-if="hasPoints">

        <!-- Info button -->
        <div class="mb-3 text-end">
            <button class="btn btn-sm btn-outline-secondary" @click="infoModalRef?.show()">
                <SvgIcon type="mdi" :path="mdiInformationOutline" style="width:16px;height:16px;vertical-align:-2px;" class="me-1" />
                How does this work?
            </button>
        </div>

        <!-- ── JOURNEY MODE ────────────────────────────────────────────────────── -->
        <template v-if="isJourneyMode">

            <!-- Stats -->
            <div class="row mb-3 g-2">
                <div class="col-6 col-md-3">
                    <div class="border rounded p-2 h-100">
                        <div class="text-muted small">GPS Distance</div>
                        <div class="fw-bold fs-5">{{ totalGpsDistance.toFixed(2) }} km</div>
                    </div>
                </div>
                <div class="col-6 col-md-3">
                    <div class="border rounded p-2 h-100">
                        <div class="text-muted small">Odometer Distance</div>
                        <div class="fw-bold fs-5">{{ odometerSpan != null ? odometerSpan.toFixed(2) + ' km' : '—' }}</div>
                    </div>
                </div>
                <div class="col-6 col-md-3">
                    <div class="border rounded p-2 h-100">
                        <div class="text-muted small">GPS Match</div>
                        <div class="fw-bold fs-5">
                            <span v-if="journeyAccuracyPct !== null" :class="`text-${accuracyClass(journeyAccuracyPct)}`">{{ journeyAccuracyPct }}%</span>
                            <span v-else class="text-muted">—</span>
                        </div>
                    </div>
                </div>
                <div class="col-6 col-md-3">
                    <div class="border rounded p-2 h-100">
                        <div class="text-muted small">GPS Points</div>
                        <div class="fw-bold fs-5">{{ allPoints.length }}</div>
                    </div>
                </div>
            </div>

            <GpsOdometerStrip
                :start-odometer="parameters.journeyOdometers?.start_odometer ?? null"
                :end-odometer="parameters.journeyOdometers?.end_odometer ?? null"
                :odometer-span="odometerSpan"
                :gps-distance="totalGpsDistance"
                :match-pct="journeyAccuracyPct"
            />

            <!-- Map -->
            <div :id="mapUuid" style="width:100%; height:440px;" class="mb-3 rounded border"></div>

            <!-- Colour legend -->
            <div class="d-flex flex-wrap gap-2 mb-3">
                <span class="badge rounded-pill px-3 py-2" style="background:#6c757d; font-size:0.78rem;">⬤ Dead running</span>
                <span
                    v-for="(b, i) in parameters.journeyBookings"
                    :key="b.uuid"
                    class="badge rounded-pill px-3 py-2"
                    :style="{ background: bookingColor(i), fontSize: '0.78rem' }"
                >⬤ {{ b.clientName ?? 'Booking ' + (i + 1) }}</span>
            </div>

            <GpsGapAnalysis :gaps="significantGaps" />
            <GpsBookingBreakdown :stats="bookingGpsStats" />

        </template>

        <!-- ── BOOKING MODE ───────────────────────────────────────────────────── -->
        <template v-else>

            <!-- Stats -->
            <div class="row mb-3 g-2">
                <div class="col-6 col-md-3">
                    <div class="border rounded p-2 h-100">
                        <div class="text-muted small">GPS Distance</div>
                        <div class="fw-bold fs-5">{{ totalGpsDistance.toFixed(2) }} km</div>
                    </div>
                </div>
                <div class="col-6 col-md-3">
                    <div class="border rounded p-2 h-100">
                        <div class="text-muted small">Odometer Distance</div>
                        <div class="fw-bold fs-5">{{ odometerDistance != null ? odometerDistance.toFixed(2) + ' km' : '—' }}</div>
                    </div>
                </div>
                <div class="col-6 col-md-3">
                    <div class="border rounded p-2 h-100">
                        <div class="text-muted small">Duration</div>
                        <div class="fw-bold fs-5">{{ segmentDuration(allPoints) }}</div>
                    </div>
                </div>
                <div class="col-6 col-md-3">
                    <div class="border rounded p-2 h-100">
                        <div class="text-muted small">GPS Points</div>
                        <div class="fw-bold fs-5">{{ allPoints.length }}</div>
                    </div>
                </div>
            </div>

            <GpsBookingTimingRow
                :pickup-time="parameters.booking?.actual_pick_up_time"
                :dropoff-time="parameters.booking?.actual_drop_off_time"
                :first-point-time="firstPointTime"
                :last-point-time="lastPointTime"
            />

            <!-- Map -->
            <div :id="mapUuid" style="width:100%; height:400px;" class="mb-3 rounded border"></div>

            <!-- Trail legend -->
            <div v-if="journeyPoints.length > 0" class="d-flex gap-3 mb-2" style="font-size:0.8rem;">
                <span><span style="display:inline-block;width:24px;height:3px;background:#007AF8;vertical-align:middle;border-radius:2px;"></span> This booking</span>
                <span><span style="display:inline-block;width:24px;height:3px;background:#5BA4F5;vertical-align:middle;border-radius:2px;"></span> Full journey trail</span>
            </div>

        </template>

        <!-- Shared info note -->
        <div class="alert alert-info mb-0 py-2" style="font-size:0.8rem;">
            <strong>How is this calculated?</strong>
            <ul class="mb-0 mt-1 ps-3">
                <li><strong>GPS Distance</strong> — sum of point-to-point haversine distances between uploaded coordinates. The drop-off window extends 5 min past the event to capture nearby points.</li>
                <li><strong>Odometer Distance</strong> — captured by the driver app's built-in GPS tracker at the exact moment of each event, independent of uploaded point density.</li>
                <li><strong>GPS Match %</strong> — how closely GPS-calculated distance matches the recorded odometer span. Low match indicates offline periods or signal loss during the journey.</li>
                <li><strong>Offline journeys</strong> — if the device was offline when the journey ended, GPS points may upload after booking events are recorded. The booking stores whichever value (GPS or odometer) was higher at the time.</li>
            </ul>
        </div>
    </template>

    <!-- Empty state -->
    <div v-else class="text-center py-5">
        <SvgIcon class="h-14 w-14 text-secondary" type="mdi" :path="mdiMapMarker" />
        <div class="fs-5 mt-2 mb-2">No GPS points found</div>
        <p class="text-muted small mb-3">
            The driver's device may have been offline, or location permissions were not active.
            Points upload automatically when connectivity is restored — try refreshing shortly.
        </p>
        <Button :loading="loading" :icon="mdiRefresh" color="primary" @click="load">Refresh</Button>
    </div>

    <!-- Sub-components that manage their own Bootstrap modal instances -->
    <GpsInfoModal ref="infoModalRef" />
</template>

<script lang="ts" setup>
import { computed, inject, nextTick, onMounted, ref, Ref, toRefs, watch } from "vue";
import { DateTime } from "luxon";

import DriverPositionUpdate from "@classes/DriverPositionUpdate";
import Button from "@components/Button.vue";
import Loading from "@components/Loading.vue";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiInformationOutline, mdiMapMarker, mdiRefresh } from "@mdi/js";

import { DriverPositionUpdateModalParameters } from "@components/DriverPositionUpdateModalPlugin";
import { bookingColor, accuracyClass, segmentDuration, useGpsAnalytics } from "./composables/useGpsAnalytics";
import { sortByTime, useGpsMapRendering } from "./composables/useGpsMapRendering";
import GpsInfoModal from "./components/GpsInfoModal.vue";
import GpsOdometerStrip from "./components/GpsOdometerStrip.vue";
import GpsGapAnalysis from "./components/GpsGapAnalysis.vue";
import GpsBookingBreakdown from "./components/GpsBookingBreakdown.vue";
import GpsBookingTimingRow from "./components/GpsBookingTimingRow.vue";

// ─── Props ────────────────────────────────────────────────────────────────────
const props = withDefaults(defineProps<{ parameters: DriverPositionUpdateModalParameters }>(), {});
const { parameters } = toRefs(props);

// ─── State ────────────────────────────────────────────────────────────────────
const loading       = ref(false);
const dataReady     = ref(false);
const allPoints     = ref<App.Models.DriverPositionUpdate[]>([]);
const journeyPoints = ref<App.Models.DriverPositionUpdate[]>([]);
const infoModalRef  = ref<InstanceType<typeof GpsInfoModal> | null>(null);

const mapContainerReady = inject<Ref<boolean>>("mapContainerReady", ref(true));

const isJourneyMode = computed(() => !parameters.value.bookingUuid && !!parameters.value.journeyUuid);
const hasPoints     = computed(() => allPoints.value.length > 0);

// ─── Analytics ────────────────────────────────────────────────────────────────
const { totalGpsDistance, odometerSpan, journeyAccuracyPct, odometerDistance, significantGaps, bookingGpsStats } =
    useGpsAnalytics(allPoints, parameters);

// ─── Map rendering ────────────────────────────────────────────────────────────
const { uuid: mapUuid, initMap } = useGpsMapRendering(allPoints, journeyPoints, parameters, bookingGpsStats);

// ─── Timing helpers for booking mode ──────────────────────────────────────────
const firstPointTime = computed(() =>
    allPoints.value[0] ? DateTime.fromISO(allPoints.value[0].position_recorded_at).toFormat('HH:mm:ss') : '—'
);
const lastPointTime = computed(() => {
    const last = allPoints.value[allPoints.value.length - 1];
    return last ? DateTime.fromISO(last.position_recorded_at).toFormat('HH:mm:ss') : '—';
});

// ─── Data loading ─────────────────────────────────────────────────────────────
const load = async () => {
    loading.value = true;
    dataReady.value = false;
    allPoints.value = [];
    journeyPoints.value = [];
    try {
        if (isJourneyMode.value) {
            const res = await DriverPositionUpdate.index({ journey: parameters.value.journeyUuid });
            allPoints.value = sortByTime(res.data);
        } else {
            const [bookingRes, journeyRes] = await Promise.all([
                DriverPositionUpdate.index({ booking: parameters.value.bookingUuid }),
                parameters.value.journeyUuid
                    ? DriverPositionUpdate.index({ journey: parameters.value.journeyUuid })
                    : Promise.resolve({ data: [] }),
            ]);
            allPoints.value = sortByTime(bookingRes.data);
            journeyPoints.value = sortByTime((journeyRes as any).data ?? []);
        }
    } finally {
        loading.value = false;
        dataReady.value = true;
    }
};

// ─── Map init — fires only when BOTH data and container are ready ──────────────
watch([dataReady, mapContainerReady], ([dReady, mReady]) => {
    if (dReady && mReady && hasPoints.value) {
        nextTick(() => initMap());
    }
});

onMounted(load);
</script>
